import { Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, Edit, Link, Redeem, Star } from '@mui/icons-material';
import moment from 'moment/moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountData, AtRiskReason, PassOut, PassService, PassStatus, RefundOwnerType } from '../../api';
import { PassPaymentStatus } from '../../api/models/PassPaymentStatus';
import { PassType } from '../../api/models/PassType';
import {
  getDaysLeft,
  getExpirationDate,
  getPassPaymentStatusIcon,
  getPassStatusIcon,
  getPunchesDisplay,
  isFixedScheduleMonthly,
  isRecurringPassType,
} from './utils';
import { CellProps, useTable } from '../../table/useTable';
import PassesActions from './PassesActions';
import { useAuth } from '../../session/InternalAuthProvider';

import { useLanguage } from '../../common/GeneralUtils';
import TimeUtils from '../../utils/TimeUtils';
import { getDateFormat, getTimeFormat } from '../../common/getDateAndTimeFormat';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import { RefundOfferDialog, RefundOfferTarget } from '../../refunds/RefundOfferDialog';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { TABLE_PRICE_ID } from '../../table/TableComponent';
import { getPassOrderPage } from '../../utils/AccountUtils';
import PassDetailDrawer from './PassDetailDrawer';
import { PassDetailPanels } from './PassDetailTypes';
import { ALL_PASS_TYPES, EXPIRING_SOON_DAYS, usePassesFilter } from '../PassesFilter/PassesFilterContext';
import { templateIdsForLocations } from '../PassesFilter/derivePassTemplateLocation';

type PassFilterFetch = {
  statuses?: PassStatus[];
  paymentStatuses?: PassPaymentStatus[];
  passTypes?: PassType[];
  orderPageIds?: string[];
  atRiskMode?: boolean;
};

const passFetcher = async ({
  search,
  page,
  perPage,
  filterState,
}: {
  search?: string;
  page: number;
  perPage: number;
  filterState?: PassFilterFetch;
}) => {
  if (filterState?.atRiskMode) {
    if (filterState.orderPageIds?.length === 0) return [];
    return PassService.getRenewalCandidatePasses(EXPIRING_SOON_DAYS, filterState.orderPageIds);
  }
  return PassService.getPasses(
    page,
    perPage,
    search,
    filterState?.passTypes,
    filterState?.statuses,
    filterState?.paymentStatuses,
    filterState?.orderPageIds,
  );
};

const AT_RISK_REASON_CHIP_COLOR: Record<AtRiskReason, 'error' | 'warning' | 'default'> = {
  [AtRiskReason.MEMBERSHIP_PAYMENT_FAILED]: 'error',
  [AtRiskReason.MEMBERSHIP_CANCELLED]: 'warning',
  [AtRiskReason.RECENTLY_EXPIRED]: 'default',
  [AtRiskReason.EXPIRING_SOON]: 'default',
  [AtRiskReason.NO_PUNCHES_LEFT]: 'default',
};

type PassesTableProps = {
  atRiskMode?: boolean;
};

const PassesTable = React.memo(({ atRiskMode = false }: PassesTableProps) => {
  const { t } = useTranslation();
  const { isAtLeastSupervisor } = UseAuthPermissions();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearchValue = searchParams.get('search') ?? undefined;

  const [deletePassIsOpen, setDeletePassIsOpen] = useState(false);
  const [refundOffer, setRefundOffer] = useState<RefundOfferTarget | undefined>(undefined);
  const [selectedPass, setSelectedPass] = useState<PassOut | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPanel, setDrawerPanel] = useState<PassDetailPanels | undefined>(undefined);
  const timeFormat = getTimeFormat(account);

  const openDrawer = (pass: PassOut, panel?: PassDetailPanels) => {
    setSelectedPass(pass);
    setDrawerPanel(panel);
    setDrawerOpen(true);
  };

  function formatCreatedAt(created_at: string) {
    const createdAt = TimeUtils.getLocalTime(created_at, account.timezone);
    const isToday = createdAt.isSame(moment(), 'day');
    return (
      <Typography
        variant={isToday ? 'body1' : 'body2'}
        color={isToday ? 'primary.main' : undefined}
        fontWeight={isToday ? 700 : undefined}>
        {createdAt.format(isToday ? timeFormat : `${getDateFormat(account).longDateFormat} ${timeFormat}`)}
      </Typography>
    );
  }

  const tableDetails: CellProps<PassOut>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (pass: PassOut) => (
        <Tooltip title={t('passes.view')} arrow>
          <IconButton onClick={() => openDrawer(pass)}>
            <Edit />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      id: 'order_number',
      alignment: 'center',
      label: t('order_number'),
      render: (pass: PassOut) => <Typography variant="body2">{`#${pass.order_number}`}</Typography>,
    },
    {
      id: 'createdat',
      alignment: 'center',
      label: t('vouchers.createdat'),
      render: (pass: PassOut) => <Typography variant="body2">{formatCreatedAt(pass.created_at)}</Typography>,
    },
    {
      id: 'name',
      alignment: 'center',
      label: t('vouchers.name'),
      render: (pass: PassOut) => (
        <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
          {pass.permanent_schedule_hold && (
            <Tooltip title={t('passes.permanent_schedule_hold_toggle')} arrow>
              <Star fontSize="small" sx={{ color: 'warning.main' }} />
            </Tooltip>
          )}
          <Typography variant="body2">{`${pass.firstname} ${pass.lastname}`}</Typography>
        </Grid>
      ),
    },
    {
      id: 'email',
      alignment: 'center',
      label: t('passes.email'),
      render: (pass: PassOut) => <Typography style={{ textTransform: 'none' }}>{pass.email}</Typography>,
    },
    {
      id: 'phone',
      alignment: 'center',
      label: t('vouchers.phone_of_buyer'),
      render: (pass: PassOut) => (
        <Typography variant="body2" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {pass.phone}
        </Typography>
      ),
    },
    {
      id: 'passProduct',
      alignment: 'center',
      label: t('passes.pass_product'),
      render: (pass: PassOut) => {
        const passOrderPage = getPassOrderPage(account, pass.order_page_id);
        const name = passOrderPage?.label[language] || pass.order_page_id;
        return (
          <Tooltip title={name} arrow>
            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
              {name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: TABLE_PRICE_ID,
      alignment: 'center',
      label: t('vouchers.price'),
    },
    {
      id: 'punches',
      alignment: 'center',
      label: t('passes.punches'),
      render: (pass: PassOut) => {
        const passOrderPage = getPassOrderPage(account, pass.order_page_id);
        return (
          <Typography variant="body2">
            {getPunchesDisplay(pass.redeemed_order_ids, passOrderPage, pass.num_punches_override)}
          </Typography>
        );
      },
    },
    {
      id: 'expiration',
      alignment: 'center',
      label: t('passes.expiration'),
      render: (pass: PassOut) => {
        const passOrderPage = getPassOrderPage(account, pass.order_page_id);
        const expirationDate = getExpirationDate(pass, passOrderPage);
        if (!expirationDate) return <Typography variant="body2">-</Typography>;
        const days = getDaysLeft(pass, passOrderPage);
        const isExpired = days !== null && days < 0;
        const isExpiringSoon = days !== null && days >= 0 && days < EXPIRING_SOON_DAYS;
        // eslint-disable-next-line no-nested-ternary
        const color = isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : undefined;
        const longLabel = isExpired
          ? t('passes.expired_days_ago', { days: Math.abs(days) })
          : t('passes.days_left_count', { days });
        return (
          <Tooltip title={days === null ? '' : longLabel} arrow>
            <Grid container alignItems="center" justifyContent="flex-start" gap={0.5} flexWrap="nowrap">
              <Typography variant="body2" sx={{ color }} fontWeight={isExpired || isExpiringSoon ? 700 : undefined}>
                {moment(expirationDate).format(getDateFormat(account).shortDateFormat)}
              </Typography>
              {days !== null && (
                <Typography variant="caption" sx={{ color: color ?? 'text.secondary' }}>
                  {t('passes.days_short', { days })}
                </Typography>
              )}
            </Grid>
          </Tooltip>
        );
      },
    },
    {
      id: 'paymentStatus',
      alignment: 'center',
      label: t('vouchers.paymentStatus'),
      render: (pass: PassOut) => getPassPaymentStatusIcon(pass.payment_status, t),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('vouchers.status'),
      render: (pass: PassOut) => getPassStatusIcon(pass, t, getPassOrderPage(account, pass.order_page_id)),
    },
    {
      id: 'actions',
      alignment: 'center',
      width: 160,
      label: t('vouchers.details'),
      render: (pass: PassOut) => {
        const shouldDisableActions = pass.status === PassStatus.ARCHIVED || !isAtLeastSupervisor;
        const passLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/passes?code=${pass.code}&n=${pass.firstname}-${
          pass.lastname
        }`;
        const passOrderPage = getPassOrderPage(account, pass.order_page_id);
        const isRecurring = isRecurringPassType(passOrderPage?.pass_type);
        // Fixed schedule monthly customers have no portal access, so there is nothing to link them to.
        const hasPortalAccess = !isFixedScheduleMonthly(passOrderPage?.pass_type);
        return (
          <Grid container direction="row" justifyContent="flex-end" padding="2px">
            {hasPortalAccess && (
              <Tooltip title={t('passes.copy_link')} arrow>
                <IconButton onClick={() => navigator.clipboard.writeText(passLink)}>
                  <Link />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('passes.redeem_pass')} arrow>
              <span>
                <IconButton
                  disabled={shouldDisableActions}
                  onClick={() => navigate('/dashboard/calendar', { state: { passToRedeem: pass } })}>
                  <Redeem />
                </IconButton>
              </span>
            </Tooltip>
            {isRecurring ? (
              // Cancelling, pausing and swapping a membership all live in the drawer's billing panel.
              <Tooltip title={t('passes.swap.manage_tooltip')} arrow>
                <span>
                  <IconButton
                    disabled={shouldDisableActions}
                    onClick={() => openDrawer(pass, PassDetailPanels.Billing)}>
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={shouldDisableActions ? t('passes.archive_disabled') : t('passes.archive')} arrow>
                <span>
                  <IconButton
                    onClick={() => {
                      setSelectedPass(pass);
                      setDeletePassIsOpen(true);
                    }}
                    disabled={shouldDisableActions}>
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Grid>
        );
      },
    },
  ];

  const atRiskReasonColumn: CellProps<PassOut> = {
    id: 'atRiskReason',
    alignment: 'center',
    label: t('passes.at_risk.reason_column'),
    render: (pass: PassOut) =>
      pass.at_risk_reason ? (
        <Chip
          size="small"
          variant="outlined"
          color={AT_RISK_REASON_CHIP_COLOR[pass.at_risk_reason]}
          label={t(`passes.at_risk.reasons.${pass.at_risk_reason}`)}
        />
      ) : null,
  };
  const nameColumnIndex = tableDetails.findIndex((column) => column.id === 'name');
  const columns = atRiskMode
    ? [...tableDetails.slice(0, nameColumnIndex + 1), atRiskReasonColumn, ...tableDetails.slice(nameColumnIndex + 1)]
    : tableDetails;

  const {
    statuses,
    paymentStatuses,
    passTypes,
    orderPageIds,
    locationIds,
    atRiskLocationIds,
    state: rawFilterState,
  } = usePassesFilter();

  const initialEntityIdParam = searchParams.get('entityid');
  useEffect(() => {
    if (!initialEntityIdParam) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const pass = await PassService.getPassById(initialEntityIdParam);
        if (cancelled) return;
        setSelectedPass(pass);
        setDrawerOpen(true);
      } catch (error) {
        if (!cancelled) console.error('Error loading pass from url param:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveFilterState = useMemo<PassFilterFetch>(() => {
    if (atRiskMode) {
      return {
        atRiskMode: true,
        orderPageIds: atRiskLocationIds === null ? undefined : templateIdsForLocations(account, atRiskLocationIds),
      };
    }

    let effectiveOrderPageIds: string[] | undefined;

    if (locationIds !== null) {
      const templatesMatchingLocation = templateIdsForLocations(account, locationIds);
      effectiveOrderPageIds =
        orderPageIds === null
          ? templatesMatchingLocation
          : templatesMatchingLocation.filter((id) => orderPageIds.includes(id));
    } else if (orderPageIds !== null) {
      effectiveOrderPageIds = orderPageIds;
    }

    // Skip the pass_types query param when all types are selected (no restriction).
    const effectivePassTypes = passTypes.length === ALL_PASS_TYPES.length ? undefined : passTypes;

    return {
      statuses,
      paymentStatuses,
      passTypes: effectivePassTypes,
      orderPageIds: effectiveOrderPageIds,
    };
    // rawFilterState kept as a dep so SWR key changes on any filter edit
  }, [
    account,
    statuses,
    paymentStatuses,
    passTypes,
    orderPageIds,
    locationIds,
    atRiskLocationIds,
    atRiskMode,
    rawFilterState,
  ]);

  const {
    mutate: mutatePasses,
    data,
    TableComponent,
  } = useTable<PassOut>({
    actionsComponent: <PassesActions atRiskMode={atRiskMode} />,
    opId: atRiskMode ? 'getRenewalCandidatePasses' : 'getPasses',
    fetcher: passFetcher,
    tableDetails: columns,
    filterState: effectiveFilterState,
    initialSearchValue,
    fillHeight: !atRiskMode,
    scrollWithPage: atRiskMode,
    hidePaging: atRiskMode,
    hideSearch: atRiskMode,
    getRowSx: (pass) =>
      pass.permanent_schedule_hold
        ? {
            backgroundColor: 'mb.claySoft',
            '& td:first-of-type': {
              boxShadow: (theme) =>
                `inset ${theme.direction === 'rtl' ? '-' : ''}4px 0 0 ${theme.palette.warning.main}`,
              paddingInlineStart: 2,
            },
          }
        : undefined,
  });
  const passesList = data || [];

  const archivePass = async () => {
    if (selectedPass) {
      await PassService.archivePass(selectedPass.id);
      const otherPasses = passesList.filter((x) => x.id !== selectedPass.id);
      await mutatePasses([...otherPasses, { ...selectedPass, status: PassStatus.ARCHIVED }]);
      setRefundOffer({
        ownerType: RefundOwnerType.PASS,
        ownerId: selectedPass.id,
        title: t('refunds.offer.passTitle'),
        customerName: `${selectedPass.firstname} ${selectedPass.lastname}`,
        ownerLabel: getPassOrderPage(account, selectedPass.order_page_id)?.label[language] ?? t('refunds.entity.pass'),
      });
    }
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      <PassDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        pass={selectedPass}
        initialExpandedPanel={drawerPanel}
        onPassUpdated={(updatedPass) => {
          setSelectedPass(updatedPass);
          const otherPasses = passesList.filter((x) => x.id !== updatedPass.id);
          void mutatePasses([...otherPasses, updatedPass]);
        }}
      />
      <ConfirmationDialog
        title={t('passes.archive_pass_title')}
        content={<Typography>{t('passes.archive_pass_description')}</Typography>}
        isOpen={deletePassIsOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed && selectedPass) {
            await archivePass();
          }
          setDeletePassIsOpen(false);
        }}
      />
      {refundOffer && <RefundOfferDialog isOpen onClose={() => setRefundOffer(undefined)} {...refundOffer} />}
    </Grid>
  );
});

export default PassesTable;
